import { useState } from 'react';
import { Ban, Trash2 } from 'lucide-react';
import type { Tenant } from '../../types/lac';
import { useNavigate } from 'react-router-dom';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksConfirmDialog } from '../../components/liks/LiksConfirmDialog/LiksConfirmDialog';
import {
  useDeleteLacTenantMutation,
  useSetLacTenantActiveMutation,
} from '../../features/lac/mutations/useLacTenantMutations';
import { useI18n } from '../../i18n/useI18n';
import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import { resolveLacTabPath } from '../../routes/lacPaths';

export type LacTenantDetailsAdminBarProps = {
  tenant: Tenant;
  tenantName: string;
};

function tenantDisplayName(tenant: Tenant): string {
  return tenant.displayName?.trim() || tenant.name;
}

export function LacTenantDetailsAdminBar({
  tenant,
  tenantName,
}: LacTenantDetailsAdminBarProps) {
  const { translate } = useI18n();
  const navigate = useNavigate();
  const deleteTenantMutation = useDeleteLacTenantMutation();
  const setActiveMutation = useSetLacTenantActiveMutation();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const isBusy = deleteTenantMutation.isPending || setActiveMutation.isPending;
  const isActive = tenant.active !== false;
  const displayName = tenantDisplayName(tenant);

  const handleToggleActive = () => {
    const nextActive = !isActive;
    setActiveMutation.mutate(
      {
        tenantName,
        active: nextActive,
      },
      {
        onSuccess: () => {
          toast.success({
            title: nextActive
              ? translate('lac.organisations.details.activateSuccessTitle')
              : translate('lac.organisations.details.deactivateSuccessTitle'),
            description: nextActive
              ? translate(
                  'lac.organisations.details.activateSuccessDescription',
                  { name: displayName },
                )
              : translate(
                  'lac.organisations.details.deactivateSuccessDescription',
                  { name: displayName },
                ),
          });
        },
        onError: (error) => {
          void toastMutationError(
            error,
            nextActive
              ? translate('lac.organisations.details.activateErrorTitle')
              : translate('lac.organisations.details.deactivateErrorTitle'),
            nextActive
              ? translate('lac.organisations.details.activateErrorDescription')
              : translate(
                  'lac.organisations.details.deactivateErrorDescription',
                ),
          );
        },
      },
    );
  };

  const handleDeleteTenant = () => {
    deleteTenantMutation.mutate(tenantName, {
      onSuccess: () => {
        toast.success({
          title: translate('lac.organisations.details.deleteSuccessTitle'),
          description: translate(
            'lac.organisations.details.deleteSuccessDescription',
            { name: displayName },
          ),
        });
        setIsDeleteConfirmOpen(false);
        navigate(resolveLacTabPath('organisations'));
      },
      onError: (error) => {
        void toastMutationError(
          error,
          translate('lac.organisations.details.deleteErrorTitle'),
          translate('lac.organisations.details.deleteErrorDescription'),
        );
      },
      onSettled: () => {
        setIsDeleteConfirmOpen(false);
      },
    });
  };

  return (
    <>
      <div className="lac-tenant-details__admin-actions">
        <LiksButton
          type="button"
          variant="secondary"
          disabled={isBusy}
          onClick={handleToggleActive}
        >
          {isActive && <Ban strokeWidth={2} aria-hidden />}
          {isActive
            ? translate('lac.organisations.details.deactivate')
            : translate('lac.organisations.details.activate')}
        </LiksButton>
        <LiksButton
          type="button"
          variant="secondary"
          className="lac-tenant-details__delete-button"
          disabled={isBusy}
          onClick={() => setIsDeleteConfirmOpen(true)}
        >
          <Trash2 strokeWidth={2} aria-hidden />
          {translate('lac.organisations.details.delete')}
        </LiksButton>
      </div>

      <LiksConfirmDialog
        isOpen={isDeleteConfirmOpen}
        title={translate('lac.organisations.details.deleteConfirmTitle')}
        message={translate('lac.organisations.details.deleteConfirmMessage', {
          name: displayName,
        })}
        confirmLabel={translate('lac.organisations.details.deleteConfirm')}
        cancelLabel={translate('lac.organisations.details.deleteCancel')}
        onConfirm={handleDeleteTenant}
        onCancel={() => {
          if (!deleteTenantMutation.isPending) {
            setIsDeleteConfirmOpen(false);
          }
        }}
      />
    </>
  );
}
